import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  useAuth,
} from '@aaas/shared';
import { PDFViewer } from '../PDFViewer';
import cvService from '../../services/cv.service';

interface PDFPreviewModalProps {
  open: boolean;
  onClose: () => void;
  cvId: string;
  attachmentId: string;
  fileName: string;
}

export function PDFPreviewModal({
  open,
  onClose,
  cvId,
  attachmentId,
  fileName,
}: PDFPreviewModalProps) {
  const { user, currentWorkspace } = useAuth();
  const { t } = useTranslation('ats');
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  // Fetch signed URL when modal opens
  useEffect(() => {
    if (!open || !user || !currentWorkspace) return;

    const fetchSignedUrl = async () => {
      setIsLoadingUrl(true);
      setUrlError(null);
      try {
        const token = await user.getIdToken();
        const data = await cvService.getAttachmentSignedUrl(
          cvId,
          attachmentId,
          currentWorkspace.id,
          token
        );
        setSignedUrl(data.signed_url);
      } catch (error) {
        console.error('Failed to fetch signed URL:', error);
        setUrlError(error instanceof Error ? error.message : t('pdfViewer.loadPdfFailed'));
      } finally {
        setIsLoadingUrl(false);
      }
    };

    fetchSignedUrl();
  }, [open, cvId, attachmentId, user, currentWorkspace, t]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSignedUrl(null);
      setUrlError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] p-0 flex flex-col">
        <DialogHeader className="px-4 sm:px-6 py-3 border-b flex-shrink-0">
          <DialogTitle className="text-sm sm:text-base truncate">
            {fileName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {isLoadingUrl ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mb-3"></div>
              <p className="text-sm text-gray-600">{t('pdfViewer.loadingPdf')}</p>
            </div>
          ) : urlError ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <p className="text-sm mb-2">{t('pdfViewer.loadPdfFailed')}</p>
              <p className="text-xs text-gray-400">{urlError}</p>
            </div>
          ) : signedUrl ? (
            <PDFViewer
              url={signedUrl}
              fileName={fileName}
              className="flex flex-col h-full bg-gray-100 overflow-hidden"
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
